import axios from 'axios';
import Button from 'react-bootstrap/Button';
import React, { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LoadingBox from '../../component/LoadingBox';
import MessageBox from '../../component/MessageBox';
import { Store } from '../../Store';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { api } from '../../Api';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        products: action.payload.products,
        pages: action.payload.pages,
        page: action.payload.page,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'DELETE_SUCCESS':
      return {
        ...state,
        products: state.products.filter(
          (product) => product._id !== action.payload
        ),
      };
    default:
      return state;
  }
};

export default function ProductListScreen() {
  const [{ loading, products, pages, error }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    products: [],
    pages: 1,
  });
  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const { search, pathname } = useLocation();
  const sp = new URLSearchParams(search);
  const page = sp.get('page') || 1;
  // This screen is mounted at both /admin/products and /agent/products, so keep
  // pagination on whichever path the user is actually on.
  const basePath = pathname.startsWith('/agent') ? '/agent/products' : '/admin/products';

  const brandNames = useMemo(
    () => new Map(brands.map((item) => [item._id, item.brandName])),
    [brands]
  );
  const categoryNames = useMemo(
    () => new Map(categories.map((item) => [item._id, item.categoryName])),
    [categories]
  );

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const { data } = await axios.get(
          api + `/api/products/admin?page=${page}`,
          {
            headers: { Authorization: `Bearer ${userInfo.token}` },
          }
        );
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (error) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
      }
    };
    fetchData();
  }, [page, userInfo]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [brandResult, categoryResult] = await Promise.all([
          axios.get(api + '/api/products/allbrands'),
          axios.get(api + '/api/products/all_categories'),
        ]);
        setBrands(brandResult.data || []);
        setCategories(categoryResult.data || []);
      } catch (error) {}
    };
    fetchMeta();
  }, []);

  const createHandler = () => {
    navigate('/admin/product/new');
  };

  const deleteHandler = async (product) => {
    if (window.confirm('Are you sure to delete?')) {
      try {
        await axios.delete(api + `/api/products/${product._id}`, {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        toast.success('Product deleted successfully');
        dispatch({ type: 'DELETE_SUCCESS', payload: product._id });
      } catch (err) {
        toast.error(getError(err));
      }
    }
  };

  return (
    <div>
      <Helmet>
        <title>Manage Products | ClickCart International Ltd.</title>
      </Helmet>
      <Container className="">
        <Row className="my-3">
          <Col>
            <h2 className=" ">Manage Products</h2>
          </Col>
          <Col className="col text-end">
            {userInfo.isAdmin && (
              <Button type="button" variant="outline-success" onClick={createHandler}>
                Add Product
              </Button>
            )}
          </Col>
        </Row>

        {loading ? (
          <LoadingBox />
        ) : error ? (
          <MessageBox variant="danger">{error}</MessageBox>
        ) : (
          <div
            className="table-responsive"
            style={{
              background: 'white',
              border: '1px solid black',
              padding: '5px',
              borderRadius: '15px',
            }}
          >
            <table className="table table-striped">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Brand</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Point</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No products found.
                    </td>
                  </tr>
                ) : (
                  products.map((product) => (
                    <tr key={product._id}>
                      <td>{product._id}</td>
                      <td>{product.name}</td>
                      <td>{brandNames.get(product.brand) || product.brand}</td>
                      <td>
                        {categoryNames.get(product.category) || product.category}
                      </td>
                      <td>{product.price}</td>
                      <td>{product.point}</td>
                      <td>{product.stock}</td>
                      <td>
                        <span
                          className={
                            product.isPublished === false
                              ? 'badge bg-secondary'
                              : 'badge bg-success'
                          }
                        >
                          {product.isPublished === false ? 'Draft' : 'Published'}
                        </span>
                      </td>
                      <td>
                        {userInfo.isAdmin ? (
                          <>
                            <Link to={`/admin/product/${product._id}`}>Edit</Link>
                            &nbsp;
                            <Button
                              type="button"
                              variant="link"
                              className="p-0 align-baseline text-danger"
                              onClick={() => deleteHandler(product)}
                            >
                              Delete
                            </Button>
                          </>
                        ) : (
                          <span className="text-muted small">View only</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div>
              {[...Array(pages).keys()].map((x) => (
                <Link
                  className={x + 1 === Number(page) ? 'btn text-bold' : 'btn'}
                  key={x + 1}
                  to={`${basePath}?page=${x + 1}`}
                >
                  {x + 1}
                </Link>
              ))}
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}
